import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  IonPage,
  IonContent,
  IonItem,
  IonInput,
  IonButton,
  IonSpinner,
  IonNote,
  IonRouterLink,
  IonIcon,
} from '@ionic/react';
import {
  mail,
  lockClosed,
  eye,
  eyeOff,
  arrowForward,
  briefcase,
  logoGoogle,
  logoApple,
  logoLinkedin,
  shieldCheckmark,
  location,
  people,
  flash,
} from 'ionicons/icons';
import { LogoLink } from '@/components/LogoLink';
import { loginAccount, requestPasswordReset, applySession } from '@/lib/api/auth';
import { AppFooter } from '@/components/AppFooter';
import { setSeo, resetSeo } from '@/lib/seo';
import { consumeReturnPath } from '@/lib/return-path';
import type { ApiError } from '@/lib/api/client';

const AUTH_ERROR_MAP: Record<string, string> = {
  'Invalid email or password': 'Email or password is incorrect. Check your credentials and try again.',
  'User not found': 'No account found with this email. Create an account first.',
  'Account disabled': 'This account has been disabled. Contact support to reactivate it.',
  'Email not verified': 'Please verify your email before signing in. Check your inbox for the verification link.',
  'Too many attempts': 'Too many sign-in attempts. Wait a few minutes and try again.',
};

function friendlyAuthError(err: unknown): string {
  if (err && typeof err === 'object' && 'status' in err) {
    const apiErr = err as ApiError;
    // Match on detail first (richer message from API), then message
    const key = apiErr.detail || apiErr.message;
    for (const [pattern, friendly] of Object.entries(AUTH_ERROR_MAP)) {
      if (key.includes(pattern)) return friendly;
    }
    // Fallback: extract readable part from "Invalid email or password" style messages
    if (apiErr.status === 401) return 'Email or password is incorrect. Check your credentials and try again.';
    if (apiErr.status === 403) return 'Access denied. Your account may not have permission.';
    if (apiErr.status === 429) return 'Too many attempts. Wait a few minutes and try again.';
    if (apiErr.status >= 500) return 'Something went wrong on our end. Please try again shortly.';
  }
  if (err instanceof Error) return err.message;
  return 'Sign in failed. Check your details and try again.';
}

/**
 * Deep-link return path (M07 S06): pages like `/sponsor/:slug` stash the
 * intended destination in sessionStorage before redirecting to sign-in.
 * M09 S02 T2.1: VerifyEmail stashes `/profile?welcome=1` after verification.
 */
const resolveReturnPath = () => consumeReturnPath();

const highlights = [
  { icon: flash, title: 'Find trade work that fits', desc: 'Matched to your skills, location & availability' },
  { icon: people, title: 'Verified pros & clients', desc: 'Real contract work — no scams, no ghost jobs' },
  { icon: location, title: 'Work near you', desc: 'Hyper-local matching across South Africa' },
];

const stats = [
  { value: '15k+', label: 'Open contracts' },
  { value: '8k+', label: 'Skilled trades' },
  { value: '50k+', label: 'Clients & businesses' },
];

export function SignInPage() {
  const history = useHistory();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSeo({ robots: 'noindex, nofollow' });
    return () => resetSeo();
  }, []);

  const handleSignIn = async () => {
    setError(null);
    if (!email || !password) {
      setError('Enter your email and password.');
      return;
    }
    setLoading(true);
    try {
      const resp = await loginAccount({ email, password });
      applySession(resp, { email, name: resp.session.name || email });
      history.replace(resolveReturnPath());
    } catch (e: unknown) {
      setError(friendlyAuthError(e));
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async () => {
    if (!email) {
      setError('Enter your email above so we can send a reset link.');
      return;
    }
    try {
      await requestPasswordReset(email);
      setError(null);
    } catch {
      /* best-effort; no user-facing failure */
    }
  };

  return (
    <IonPage>
      <IonContent className="bp-auth-content" fullscreen>
        <div className="bp-auth-shell">
          {/* ── Brand panel ── */}
          <aside className="bp-auth-brand">
            <div className="bp-glow-orb bp-auth-glow-1" />
            <div className="bp-glow-orb bp-auth-glow-2" />
            <div className="bp-auth-brand-inner">
              <LogoLink className="bp-auth-logo" />

              <div className="bp-auth-brand-head">
                <div className="bp-badge">
                  <IonIcon icon={flash} style={{ fontSize: '0.625rem' }} />
                  South Africa's Contract Marketplace
                </div>
                <h1 className="bp-auth-brand-title">
                  Welcome back to{' '}
                  <span className="bp-auth-brand-accent">BluePages</span>
                </h1>
                <p className="bp-auth-brand-sub">
                  Sign in to pick up where you left off — your matches, messages,
                  and contract work are waiting.
                </p>
              </div>

              <ul className="bp-auth-highlights">
                {highlights.map((h) => (
                  <li key={h.title} className="bp-auth-highlight">
                    <span className="bp-auth-highlight-icon">
                      <IonIcon icon={h.icon} />
                    </span>
                    <div>
                      <div className="bp-auth-highlight-title">{h.title}</div>
                      <div className="bp-auth-highlight-desc">{h.desc}</div>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="bp-auth-stats">
                {stats.map((s) => (
                  <div key={s.label} className="bp-auth-stat">
                    <div className="bp-auth-stat-value">{s.value}</div>
                    <div className="bp-auth-stat-label">{s.label}</div>
                  </div>
                ))}
              </div>

              <div className="bp-auth-trust">
                <IonIcon icon={shieldCheckmark} />
                <span>Verified pros &amp; clients · POPIA-compliant</span>
              </div>
            </div>
          </aside>

          {/* ── Auth panel ── */}
          <main className="bp-auth-panel">
            <div className="bp-auth-card">
              <div className="bp-auth-card-head">
                <h2 className="bp-auth-title">Sign in</h2>
                <p className="bp-auth-subtitle">
                  New to BluePages?{' '}
                  <IonRouterLink routerLink="/register" className="bp-auth-link">
                    Create an account
                  </IonRouterLink>
                </p>
              </div>

              <div className="bp-auth-social">
                <button className="bp-auth-social-btn" type="button" onClick={() => history.replace(resolveReturnPath())}>
                  <IonIcon icon={logoGoogle} /> Google
                </button>
                <button className="bp-auth-social-btn" type="button" onClick={() => history.replace(resolveReturnPath())}>
                  <IonIcon icon={logoApple} /> Apple
                </button>
              </div>

              <div className="bp-auth-divider">
                <span>or continue with email</span>
              </div>

              <div className="bp-auth-form">
                <div className="bp-auth-field">
                  <label className="bp-auth-label">Email</label>
                  <IonItem className="bp-auth-input" lines="none">
                    <IonIcon icon={mail} slot="start" className="bp-auth-input-icon" />
                    <IonInput
                      type="email"
                      autocomplete="email"
                      placeholder="you@email.com"
                      value={email}
                      onIonInput={(e) => setEmail(e.detail.value!)}
                      onKeyPress={(e: any) => e.key === 'Enter' && handleSignIn()}
                    />
                  </IonItem>
                </div>

                <div className="bp-auth-field">
                  <div className="bp-auth-label-row">
                    <label className="bp-auth-label">Password</label>
                    <IonRouterLink routerLink="/verify-email" className="bp-auth-forgot" onClick={handleForgot}>
                      Forgot password?
                    </IonRouterLink>
                  </div>
                  <IonItem className="bp-auth-input" lines="none">
                    <IonIcon icon={lockClosed} slot="start" className="bp-auth-input-icon" />
                    <IonInput
                      type={showPassword ? 'text' : 'password'}
                      autocomplete="current-password"
                      placeholder="••••••••"
                      value={password}
                      onIonInput={(e) => setPassword(e.detail.value!)}
                      onKeyPress={(e: any) => e.key === 'Enter' && handleSignIn()}
                    />
                    <IonIcon
                      icon={showPassword ? eyeOff : eye}
                      slot="end"
                      className="bp-auth-input-icon bp-auth-input-toggle"
                      onClick={() => setShowPassword((v) => !v)}
                    />
                  </IonItem>
                </div>

                {error && <IonNote color="danger" className="bp-auth-error">{error}</IonNote>}

                <IonButton
                  expand="block"
                  className="bp-auth-submit"
                  onClick={handleSignIn}
                  disabled={loading}
                >
                  {loading ? (
                    <IonSpinner name="dots" />
                  ) : (
                    <>
                      Sign in <IonIcon icon={arrowForward} slot="end" />
                    </>
                  )}
                </IonButton>
              </div>

              <div className="bp-auth-alt">
                <span>Don't have an account yet?</span>
                <IonRouterLink routerLink="/register" className="bp-auth-link">
                  <IonIcon icon={briefcase} /> Post your trade &amp; get hired
                </IonRouterLink>
              </div>

              <div className="bp-auth-social-row">
                <IonIcon icon={logoLinkedin} />
                <IonIcon icon={logoGoogle} />
                <IonIcon icon={logoApple} />
              </div>
            </div>
          </main>
        </div>
        <AppFooter />
      </IonContent>
    </IonPage>
  );
}
